import type { SidebarNavChildItem, SidebarNavFeature } from "@/config/sidebar-nav";

/**
 * The three dashboards of the platform sketch (Oct 2026): the owner's full
 * view, the department head's "avd" view and the employee's "mitt" view.
 * An owner or admin may preview the two smaller ones through the "Visa som"
 * switch; everyone else gets the one their membership maps to.
 */
export type ViewAsRole = "owner" | "head" | "employee";
export const VIEW_AS_ROLES: readonly ViewAsRole[] = ["owner", "head", "employee"];

export function isViewAsRole(value: unknown): value is ViewAsRole {
  return typeof value === "string" && (VIEW_AS_ROLES as readonly string[]).includes(value);
}

/**
 * Which nav features a role keeps, and for a tabbed feature which of its
 * sub-pages. A feature listed with `null` keeps every child; one listed with
 * a filter keeps the children whose `titleKey` or `url` matches. Settings is
 * behind the footer gear rather than in the nav, so it passes through for
 * every role (`hiddenFromNav`).
 */
type ChildFilter = { titleKeys: readonly string[]; urls: readonly string[] } | null;

const HEAD_FEATURES: Record<string, ChildFilter> = {
  overview: null,
  playbook: null,
  goals: null,
  // "Mål" in a sales business is the KPI page.
  performance: null,
  routines: null,
  teams: {
    titleKeys: ["salesTeam", "people", "checkIn"],
    urls: ["/coach/teams", "/people", "/coach/performance/pulse"],
  },
  coach: null,
  reports: { titleKeys: ["reports", "grossProfit"], urls: ["/reports", "/bruttovinst"] },
};

const EMPLOYEE_FEATURES: Record<string, ChildFilter> = {
  overview: null,
  routines: { titleKeys: ["today"], urls: ["/coach/routines/today"] },
  coach: null,
};

const FEATURES_BY_ROLE: Record<Exclude<ViewAsRole, "owner">, Record<string, ChildFilter>> = {
  head: HEAD_FEATURES,
  employee: EMPLOYEE_FEATURES,
};

function keepChild(child: SidebarNavChildItem, filter: ChildFilter): boolean {
  if (!filter) return true;
  return filter.titleKeys.includes(child.titleKey) || filter.urls.includes(child.url);
}

/**
 * Narrows the visible nav to what `role` sees. Pure: the owner's list passes
 * through untouched; the other roles keep the features in their table, with
 * tabbed features cut down to the allowed sub-pages. A tabbed feature left
 * without a sub-page and without its own url is dropped, since it would have
 * nothing to link to.
 */
export function applyViewAs(
  features: SidebarNavFeature[],
  role: ViewAsRole,
): SidebarNavFeature[] {
  if (role === "owner") return features;
  const table = FEATURES_BY_ROLE[role];
  const out: SidebarNavFeature[] = [];
  for (const feature of features) {
    if (feature.hiddenFromNav) {
      out.push(feature);
      continue;
    }
    if (!(feature.id in table)) continue;
    const filter = table[feature.id] ?? null;
    if (!feature.children?.length || !filter) {
      out.push(feature);
      continue;
    }
    const children = feature.children.filter((c) => keepChild(c, filter));
    if (children.length === 0 && !feature.url) continue;
    out.push({ ...feature, children });
  }
  return out;
}
